import { Component, DestroyRef, OnInit, Signal, WritableSignal, computed, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { BehaviorSubject, EMPTY, Observable, Subject, from, merge, of } from 'rxjs';
import { catchError, concatMap, debounceTime, filter, finalize, map, mergeMap, take, tap } from 'rxjs/operators';

import { UploadProgressService } from 'src/app/core/services/upload-progress.service';
import { indicate } from 'src/app/operators';
import { ConfirmDialogComponent } from 'src/app/shared/confirm-dialog/confirm-dialog.component';
import {
  ApiException,
  DocumentIndexColumnViewModel,
  DocumentIndexRowViewModel,
  DocumentUploadCommitFailureViewModel,
  DocumentUploadCommitRequest,
  DocumentUploadCommitResultViewModel,
  DocumentUploadFileState,
  DocumentUploadFileViewModel,
  DocumentUploadIssueSeverity,
  DocumentUploadIssueViewModel,
  DocumentUploadJobState,
  DocumentUploadPreviewViewModel,
  DocumentUploadStatusViewModel,
  DocumentUploadUpdateViewModel,
  DocumentUploadValidationViewModel,
  EsraPipingAPIClient,
  FileParameter,
  ShareLinkClass,
  UpdateDocumentIndexRowsRequest,
} from 'src/app/shared/models/autogenerated-piping';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { getApiErrorMessage } from 'src/app/shared/utils';
import {
  ALLOWED_DOCUMENT_EXTENSIONS,
  createUploadBatches,
  describeAmbiguousFileName,
  DOCUMENT_UPLOAD_MAX_CONCURRENT_REQUESTS,
  FILE_NAME_COLUMN_KEY,
  filterAcceptedDocuments,
  INDEX_FILE_EXTENSION,
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_MB,
  normalizeFileName,
  splitAmbiguousFileNames,
} from '../bu-pdf-bulk-upload.constants';
import {
  AttemptState,
  buildReviewSummary,
  buildRowViews,
  computeProcessingProgress,
  computeRequiredProgress,
  describeRejection,
  describeNewRevisions,
  describeRecordCount,
  DisplayFile,
  FILE_MESSAGES,
  FileCandidate,
  PreviewRow,
  ProcessingProgress,
  RowCandidate,
  RowPickerResult,
  RequiredProgress,
  ReviewInputs,
  ReviewSummary,
  ROW_FILE_STATE_ICONS,
  ROW_FILE_STATE_LABELS,
  RowFileState,
  RowSort,
  RowView,
  sortRowViews,
  UploadAttempt,
} from '../bu-pdf-bulk-upload-review';
import { BuPdfFilePickerDialogComponent } from '../bu-pdf-file-picker-dialog/bu-pdf-file-picker-dialog.component';
import { BuPdfRowPickerDialogComponent } from '../bu-pdf-row-picker-dialog/bu-pdf-row-picker-dialog.component';

/** Pause after the last keystroke before the edited rows are sent for validation. */
const ROW_SAVE_DEBOUNCE_MS: number = 500;
const DEFAULT_PAGE_SIZE: number = 50;

/** Where a document of this page is while the browser sends it. The server's own state is separate (`DocumentUploadFileState`). */
type TransferState = 'uploading' | 'uploaded' | 'failed' | 'rejected';

/** What this page knows about one document it sent (or is sending), keyed by the normalized file name. */
interface TransferEntry {
  fileName: string;
  sizeInBytes: number;
  state: TransferState;
  message?: string;
}

/** The sections the user can expand and collapse. The index table is not one of them. */
export type SectionId = 'review' | 'activity' | 'unreferenced' | 'storedUnreferenced';

/** A file that was uploaded and stored for this upload, but that no row of the index names any more. Only the user can remove it. */
export interface StoredUnreferencedFile {
  key: string;
  fileName: string;
}

/** A file the user selected that no row of the index refers to. It was not uploaded, so the server knows nothing about it. */
export interface UnreferencedFile {
  key: string;
  fileName: string;
  message: string;
  /** True while the page still holds the file, so it can be associated to a row and sent. After a refresh it cannot. */
  canAssociate: boolean;
}

/** The result of one row save, so an association can wait for the save that carries its change. */
interface RowSaveResult {
  version: number;
  ok: boolean;
}

interface IssueIndex {
  byCell: Map<string, DocumentUploadIssueViewModel[]>;
  byRow: Map<number, DocumentUploadIssueViewModel[]>;
  byFile: Map<string, DocumentUploadIssueViewModel[]>;
  general: DocumentUploadIssueViewModel[];
}

const NO_ISSUES: ReadonlyArray<DocumentUploadIssueViewModel> = [];

const EMPTY_VALIDATION: DocumentUploadValidationViewModel = new DocumentUploadValidationViewModel({ issues: [], errorCount: 0, warningCount: 0 });
const EMPTY_STATUS: DocumentUploadStatusViewModel = new DocumentUploadStatusViewModel({
  state: DocumentUploadJobState.Preview,
  files: [],
  totalFiles: 0,
  completedFiles: 0,
  completedBytes: 0,
  areAllFilesUploaded: true,
  createdRecordIds: [],
});

function cellKey(rowId: number, columnKey: string): string {
  return `${rowId}|${columnKey}`;
}

function fileKey(fileName: string): string {
  return normalizeFileName(fileName);
}

function addToGroup<TKey>(map: Map<TKey, DocumentUploadIssueViewModel[]>, key: TKey, issue: DocumentUploadIssueViewModel): void {
  const group: DocumentUploadIssueViewModel[] | undefined = map.get(key);
  if (group) {
    group.push(issue);
  } else {
    map.set(key, [issue]);
  }
}

/** Indexes the validation issues once per validation result so every cell lookup in the template is O(1). */
function buildIssueIndex(issues: ReadonlyArray<DocumentUploadIssueViewModel>): IssueIndex {
  const index: IssueIndex = { byCell: new Map(), byRow: new Map(), byFile: new Map(), general: [] };

  for (const issue of issues) {
    if (issue.rowId !== undefined && issue.rowId !== null) {
      addToGroup(index.byRow, issue.rowId, issue);
      if (issue.columnKey) {
        addToGroup(index.byCell, cellKey(issue.rowId, issue.columnKey), issue);
      }
    } else if (issue.fileName) {
      addToGroup(index.byFile, fileKey(issue.fileName), issue);
    } else {
      index.general.push(issue);
    }
  }

  return index;
}

function toPreviewRows(rows: ReadonlyArray<DocumentIndexRowViewModel> | undefined): PreviewRow[] {
  return (rows ?? []).map((row: DocumentIndexRowViewModel) => ({ rowId: row.rowId ?? 0, values: [...(row.values ?? [])] }));
}

/**
 * Editable preview of a BU document upload: shows the index rows like the Excel template, validates them (inline and as a
 * summary) every time they change, sends the documents in bounded batches while the user reviews, and creates the records only on
 * confirmation.
 *
 * Why this exists: the records are created from user-supplied files, so the user gets to see and fix every problem first.
 * How it works: the index request already returned this preview with every expected document missing. Row edits are debounced and
 * sent in order (`concatMap`, so an older response can never overwrite a newer one); the server validates the whole set again
 * because some rules compare rows with each other. The documents are sent by the browser in batches of a few files with a few
 * requests in flight (`mergeMap`); each file is reported uploaded only after the server checked, stored and recorded it, a failed
 * file can be retried on its own, and a file that is already uploaded is not sent again. Confirming is one foreground request:
 * the page is blocked while it runs and shows its real result, success or a structured failure. The job lives on the server,
 * so leaving or refreshing the page never discards it; it can be resumed until it expires (the files to retry are then selected again).
 *
 * The index table is the one place that shows a row and its file together: the file of a row is chosen with a file (never typed), and the
 * rows with problems come first. Progress is two separate facts: whether every file the user selected reached an end (uploaded, refused
 * or failed), and whether every file the index requires is uploaded. A file no row refers to is refused before anything is stored; the
 * page keeps it so the user can associate it to a row (the rows are saved first, the file is sent only afterwards).
 * Internal terms never reach the user: server states are mapped to the wording in `bu-pdf-bulk-upload-review.ts`.
 */
@Component({
  selector: 'app-bu-pdf-bulk-upload-preview',
  templateUrl: './bu-pdf-bulk-upload-preview.component.html',
  styleUrls: ['./bu-pdf-bulk-upload-preview.component.scss'],
  standalone: false,
})
export class BuPdfBulkUploadPreviewComponent implements OnInit {
  private readonly esraPipingApiClient: EsraPipingAPIClient = inject(EsraPipingAPIClient);
  private readonly snackBarService: SnackBarService = inject(SnackBarService);
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly uploadProgressService: UploadProgressService = inject(UploadProgressService);

  /** The job returned when the upload was started (or restored). */
  readonly preview = input.required<DocumentUploadPreviewViewModel>();
  /**
   * The documents the user already selected next to the index file. They are sent right after the preview opens; empty when the
   * job was restored after a refresh (the browser no longer has the files, so the user selects them again).
   */
  readonly initialDocuments = input<ReadonlyArray<File>>([]);
  /** Emits whether the job still holds work the user has not confirmed. */
  readonly activeChange = output<boolean>();
  /** Emits when the user leaves the preview; `true` when the records were created. */
  readonly closed = output<boolean>();

  // Exposed for the template.
  readonly FileState = DocumentUploadFileState;
  readonly JobState = DocumentUploadJobState;
  readonly Severity = DocumentUploadIssueSeverity;
  readonly allowedDocumentExtensions: ReadonlyArray<string> = ALLOWED_DOCUMENT_EXTENSIONS;
  readonly maxFileSizeMb: number = MAX_FILE_SIZE_MB;
  readonly pageSizeOptions: ReadonlyArray<number> = [25, 50, 100];

  // ── State ──────────────────────────────────────────────────────────────────
  readonly jobId: WritableSignal<string> = signal<string>('');
  readonly type: WritableSignal<ShareLinkClass> = signal<ShareLinkClass>(ShareLinkClass.PdfPipeClass);
  readonly indexFileName: WritableSignal<string> = signal<string>('');
  readonly columns: WritableSignal<DocumentIndexColumnViewModel[]> = signal<DocumentIndexColumnViewModel[]>([]);
  readonly rows: WritableSignal<PreviewRow[]> = signal<PreviewRow[]>([]);
  readonly validation: WritableSignal<DocumentUploadValidationViewModel> = signal<DocumentUploadValidationViewModel>(EMPTY_VALIDATION);
  readonly status: WritableSignal<DocumentUploadStatusViewModel> = signal<DocumentUploadStatusViewModel>(EMPTY_STATUS);
  readonly pageIndex: WritableSignal<number> = signal<number>(0);
  readonly pageSize: WritableSignal<number> = signal<number>(DEFAULT_PAGE_SIZE);
  /** The job is gone on the server (expired); nothing can be edited any more. */
  readonly isJobLost: WritableSignal<boolean> = signal<boolean>(false);
  readonly isDraggingDocuments: WritableSignal<boolean> = signal<boolean>(false);

  /** Row edits not yet confirmed as saved and validated by the server. */
  private readonly hasPendingRowChanges: WritableSignal<boolean> = signal<boolean>(false);
  private readonly isSavingRows: WritableSignal<boolean> = signal<boolean>(false);
  /** True while the index file is being replaced; also drives the transfer progress bar. */
  readonly isFileOperationRunning: WritableSignal<boolean> = signal<boolean>(false);
  /** What this page knows about each document it sent, keyed by the normalized file name. Cleared as the server confirms. */
  private readonly transfers: WritableSignal<Map<string, TransferEntry>> = signal<Map<string, TransferEntry>>(new Map<string, TransferEntry>());
  /**
   * Every file the user selected in this page session, with how its latest attempt ended. Unlike `transfers` it is never cleared by the
   * server's confirmation, so the progress can say how many selected files were processed.
   */
  private readonly attempts: WritableSignal<Map<string, UploadAttempt>> = signal<Map<string, UploadAttempt>>(new Map<string, UploadAttempt>());
  /**
   * The files the page still holds, keyed like `transfers`: the ones whose upload failed or was refused, so they can be sent again, and
   * the ones no row refers to, so they can be associated to a row. Only the browser has them; a refresh loses them.
   */
  private readonly retainedFiles: Map<string, File> = new Map<string, File>();
  /** Number of document requests currently running. */
  private readonly runningTransferRequests: WritableSignal<number> = signal<number>(0);
  /** True while at least one document request is running. */
  readonly isTransferring: Signal<boolean> = computed(() => this.runningTransferRequests() > 0);
  /** True once the user changed a row, so replacing the index file asks before discarding the edits. */
  private readonly hasRowEdits: WritableSignal<boolean> = signal<boolean>(false);
  private readonly rowChanges$: Subject<void> = new Subject<void>();
  /** Asks for an immediate save of the rows, for an association that must wait for the server to know the new file name. */
  private readonly flushRows$: Subject<void> = new Subject<void>();
  /** Emits when a row save ends, so an association can wait for the save that carries its change. */
  private readonly rowSaved$: Subject<RowSaveResult> = new Subject<RowSaveResult>();
  /** The edit version of the last row save the server accepted; a save of nothing new is skipped. */
  private lastSavedVersion: number = 0;
  /** The row a file is being selected for, while the file dialog of the browser is open. */
  private rowFileTarget: number | null = null;
  /** The order rows had when an upload started, so rows do not jump around while the files arrive. */
  private frozenOrder: number[] | null = null;
  private previousRowViews: Map<number, RowView> = new Map<number, RowView>();
  /** The sections the user opened or closed by hand; a section without an entry follows its default. */
  readonly sectionChoices: WritableSignal<Partial<Record<SectionId, boolean>>> = signal<Partial<Record<SectionId, boolean>>>({});
  /** What the table is sorted by. Status ascending is the default order (problems first), so the header shows it as active. */
  readonly sortState: WritableSignal<RowSort> = signal<RowSort>({ active: 'status', direction: 'asc' });
  /** Incremented per edit; a save response only clears the pending flag when no newer edit happened meanwhile. */
  private editVersion: number = 0;
  /**
   * Incremented whenever the user (or a confirmation) changes the job. A response to a read that started before the change is
   * stale, so it is dropped instead of overwriting newer state (validation, files, rows).
   */
  private requestEpoch: number = 0;
  private isClosed: boolean = false;
  /** The server's answer to "can this be confirmed right now"; only a hint, the confirmation is revalidated by the server. */
  private readonly serverCanCommit: WritableSignal<boolean> = signal<boolean>(false);
  /** True while the confirmation request runs; the page is blocked until the server answers. */
  readonly isCommitting: WritableSignal<boolean> = signal<boolean>(false);

  /** Drives the confirm button spinner and double-submit protection. */
  readonly confirming$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  readonly uploadProgress: Signal<number> = this.uploadProgressService.progress;

  // -- Derived state ---------------------------------------------------------
  readonly jobState: Signal<DocumentUploadJobState> = computed(() => this.status().state ?? DocumentUploadJobState.Preview);
  readonly isEditable: Signal<boolean> = computed(() => this.jobState() === DocumentUploadJobState.Preview && !this.isJobLost() && !this.isCommitting());
  readonly errorCount: Signal<number> = computed(() => this.validation().errorCount ?? 0);
  readonly warningCount: Signal<number> = computed(() => this.validation().warningCount ?? 0);
  readonly isBusy: Signal<boolean> = computed(
    () => this.isSavingRows() || this.hasPendingRowChanges() || this.isFileOperationRunning() || this.isCommitting());
  readonly files: Signal<DocumentUploadFileViewModel[]> = computed(() => this.status().files ?? []);
  /** The documents the server lists (the ones the index needs, missing until uploaded), adjusted by what this page is doing with each file. */
  readonly displayFiles: Signal<DisplayFile[]> = computed(() => this.mergeTransfers(this.files(), this.transfers()));
  private readonly filesByKey: Signal<Map<string, DisplayFile>> = computed(
    () => new Map<string, DisplayFile>(this.displayFiles().map((file: DisplayFile): [string, DisplayFile] => [fileKey(file.fileName), file])));
  readonly fileNameColumnIndex: Signal<number> = computed(() => this.columns().findIndex((column: DocumentIndexColumnViewModel) => column.key === FILE_NAME_COLUMN_KEY));
  private readonly columnKeys: Signal<string[]> = computed(() => this.columns().map((column: DocumentIndexColumnViewModel) => column.key ?? ''));
  /** The distinct files the rows refer to; the only files that count toward being ready to confirm. */
  readonly requiredFileKeys: Signal<Set<string>> = computed(() => {
    const index: number = this.fileNameColumnIndex();
    const keys: Set<string> = new Set<string>();
    if (index >= 0) {
      for (const row of this.rows()) {
        const key: string = fileKey(row.values[index] ?? '');
        if (key) {
          keys.add(key);
        }
      }
    }
    return keys;
  });
  /** How the files the user selected ended up. Rejected and failed files are finished too. */
  readonly processing: Signal<ProcessingProgress> = computed(() => computeProcessingProgress(this.attempts().values(), this.runningTransferRequests()));
  /** How many of the files the index needs are uploaded. Files the index does not use are not counted. */
  readonly required: Signal<RequiredProgress> = computed(() => computeRequiredProgress(this.requiredFileKeys(), (key: string) => {
    const file: DisplayFile | undefined = this.filesByKey().get(key);
    return file?.state === DocumentUploadFileState.Uploaded && !file.isUploading;
  }));
  readonly areAllRequiredFilesUploaded: Signal<boolean> = computed(() => this.required().areAllUploaded);
  /** Share of the selected files that reached an end, whatever that end was. */
  readonly processedPercent: Signal<number> = computed(() => {
    const progress: ProcessingProgress = this.processing();
    return progress.attemptedCount > 0 ? Math.floor(100 * progress.processedCount / progress.attemptedCount) : 0;
  });
  /** Failed attempts the page can send again. */
  readonly hasFailedUploads: Signal<boolean> = computed(() => this.processing().failedCount > 0);
  readonly displayedColumns: Signal<string[]> = computed(() => ['status', 'excelRow', ...this.columns().map((column: DocumentIndexColumnViewModel) => column.key ?? ''), 'actions']);
  private readonly issueIndex: Signal<IssueIndex> = computed(() => buildIssueIndex(this.validation().issues ?? []));
  /** One view per row, rebuilt in one pass; a row whose inputs did not change keeps its previous object. */
  readonly rowViews: Signal<RowView[]> = computed(() => {
    const views: RowView[] = buildRowViews(this.rows(), this.fileNameColumnIndex(), this.filesByKey(), (rowId: number) => this.getRowIssues(rowId), this.previousRowViews);
    this.previousRowViews = new Map<number, RowView>(views.map((view: RowView): [number, RowView] => [view.row.rowId, view]));
    return views;
  });
  /** The rows in the order they are shown. Presentation only: the rows and their order on the server are untouched. */
  readonly sortedRowViews: Signal<RowView[]> = computed(() => {
    const sorted: RowView[] = sortRowViews(this.rowViews(), this.sortState(), this.columnKeys());
    if (!this.isTransferring()) {
      this.frozenOrder = null;
      return sorted;
    }
    // While files arrive the rows keep the order they had, so the table does not jump under the user's eyes.
    const frozen: number[] = this.frozenOrder ?? sorted.map((view: RowView) => view.row.rowId);
    this.frozenOrder = frozen;
    const placeById: Map<number, number> = new Map<number, number>(frozen.map((rowId: number, place: number): [number, number] => [rowId, place]));
    return [...sorted].sort((a: RowView, b: RowView) => (placeById.get(a.row.rowId) ?? Number.MAX_SAFE_INTEGER) - (placeById.get(b.row.rowId) ?? Number.MAX_SAFE_INTEGER) || a.excelRow - b.excelRow);
  });
  readonly pagedRowViews: Signal<RowView[]> = computed(() => {
    const start: number = this.pageIndex() * this.pageSize();
    return this.sortedRowViews().slice(start, start + this.pageSize());
  });
  /** Files the user selected that no row refers to: not uploaded, and only the browser knows them. */
  readonly unreferencedFiles: Signal<UnreferencedFile[]> = computed(() => {
    const required: Set<string> = this.requiredFileKeys();
    const items: UnreferencedFile[] = [];
    for (const [key, attempt] of this.attempts()) {
      if ((attempt.state === 'rejected' || attempt.state === 'failed') && !required.has(key)) {
        items.push({ key, fileName: attempt.fileName, message: attempt.message ?? FILE_MESSAGES.notReferenced, canAssociate: this.retainedFiles.has(key) });
      }
    }
    return items;
  });
  readonly rowsWithoutFileName: Signal<number> = computed(() => this.rowViews().filter((view: RowView) => view.fileState === 'none').length);
  /** Rows that create a new release of a current record. Information only: it is not part of the review issues. */
  readonly newRevisionText: Signal<string> = computed(() => {
    const count: number = this.rowViews().filter((view: RowView) => view.isNewRevision).length;
    return count > 0 ? describeNewRevisions(count) : '';
  });
  /**
   * Files stored for this upload that no row names any more (a row was edited or removed, or the index was replaced). They stay until the user
   * decides: nothing removes them automatically.
   */
  readonly storedUnreferencedFiles: Signal<StoredUnreferencedFile[]> = computed(() => {
    const required: Set<string> = this.requiredFileKeys();
    return this.displayFiles()
      .filter((file: DisplayFile) => file.state === DocumentUploadFileState.Uploaded && !file.isUploading && !required.has(fileKey(file.fileName)))
      .map((file: DisplayFile): StoredUnreferencedFile => ({ key: fileKey(file.fileName), fileName: file.fileName }));
  });
  /** The counts of what needs attention, by kind. */
  private readonly reviewInputs: Signal<ReviewInputs> = computed(() => {
    const views: RowView[] = this.rowViews();
    const files: DisplayFile[] = this.displayFiles();
    const attempts: UploadAttempt[] = [...this.attempts().values()];
    const repeatedKeys: Set<string> = new Set<string>(attempts.filter((attempt: UploadAttempt) => attempt.isRepeatedName).map((attempt: UploadAttempt) => normalizeFileName(attempt.fileName)));
    const issues: DocumentUploadIssueViewModel[] = this.validation().issues ?? [];
    let otherIssues: number = 0;
    let warningCount: number = 0;
    for (const issue of issues) {
      if (issue.severity === DocumentUploadIssueSeverity.Warning && issue.columnKey !== FILE_NAME_COLUMN_KEY) {
        warningCount++;
      }
      if (issue.severity === DocumentUploadIssueSeverity.Error && (issue.rowId === undefined || issue.rowId === null) && !issue.fileName) {
        otherIssues++;
      }
    }

    return {
      rowsNeedingFile: views.filter((view: RowView) => view.fileState === 'none' || view.fileState === 'missing').length,
      rowsWithErrors: views.filter((view: RowView) => view.dataErrorCount > 0).length,
      warningCount,
      filesFailed: files.filter((file: DisplayFile) => file.state === DocumentUploadFileState.Failed && !file.isUploading).length,
      filesNotValid: files.filter((file: DisplayFile) => file.state === DocumentUploadFileState.Rejected && !file.isUploading).length,
      filesNotReferenced: this.unreferencedFiles().filter((item: UnreferencedFile) => !repeatedKeys.has(item.key)).length,
      filesRepeated: attempts.filter((attempt: UploadAttempt) => attempt.isRepeatedName && attempt.state === 'rejected').length,
      filesNoLongerUsed: this.storedUnreferencedFiles().length,
      otherIssues,
      filesUploading: this.processing().uploadingCount,
    };
  });
  /** Everything that needs the user's attention, grouped by kind; healthy only when every group is empty. */
  readonly reviewSummary: Signal<ReviewSummary> = computed(() => buildReviewSummary(this.reviewInputs()));
  readonly canConfirm: Signal<boolean> = computed(
    () => this.isEditable() && !this.isBusy() && !this.isTransferring() && this.areAllRequiredFilesUploaded() && this.rowsWithoutFileName() === 0
      && this.serverCanCommit() && this.errorCount() === 0 && this.rows().length > 0);
  /** Why the confirm button is disabled, shown as its tooltip; empty when it is enabled. */
  readonly confirmDisabledReason: Signal<string> = computed(() => {
    if (this.canConfirm()) {
      return '';
    }
    if (this.isTransferring()) {
      return 'Wait until the files are uploaded.';
    }
    if (this.isBusy()) {
      return 'Wait until your changes are validated.';
    }
    if (this.rows().length === 0) {
      return 'Add at least one row.';
    }
    if (this.rowsWithoutFileName() > 0) {
      return 'Select a file for every row.';
    }
    if (this.errorCount() > 0) {
      return `Fix the ${this.errorCount()} error(s) first.`;
    }
    if (this.hasFailedUploads()) {
      return 'Retry or select again the files that could not be uploaded.';
    }
    if (!this.areAllRequiredFilesUploaded()) {
      return 'Upload the files that are still missing first.';
    }
    return 'Wait until the upload is checked.';
  });

  // -- Upload activity wording ------------------------------------------------
  /** The short state shown in the heading of the upload activity. */
  readonly activityState: Signal<string> = computed(() => {
    const progress: ProcessingProgress = this.processing();
    if (progress.attemptedCount === 0) {
      return 'No files selected';
    }
    if (!progress.isFinished) {
      return 'In progress';
    }
    return progress.needsAttentionCount > 0 ? 'Finished with issues' : 'Finished';
  });
  /** The lines under the progress bar: files processed, uploaded and needing attention, then the files the index needs. */
  readonly activityLines: Signal<string[]> = computed(() => {
    const progress: ProcessingProgress = this.processing();
    const required: RequiredProgress = this.required();
    const lines: string[] = [];
    if (progress.attemptedCount > 0) {
      lines.push(progress.isFinished ? 'Upload finished' : 'Uploading files...');
      lines.push(`${progress.processedCount} of ${progress.attemptedCount} files processed`);
      lines.push(`${progress.uploadedCount} uploaded`);
      if (progress.needsAttentionCount > 0) {
        lines.push(`${progress.needsAttentionCount} ${progress.needsAttentionCount === 1 ? 'file needs' : 'files need'} attention`);
      }
    }
    lines.push(`${required.requiredUploadedCount} of ${required.requiredFileCount} required files uploaded`);
    return lines;
  });
  /** Failed or refused files of this session that rows refer to; the ones no row refers to have a section of their own. */
  readonly attentionFiles: Signal<{ key: string; fileName: string; message: string; canRetry: boolean }[]> = computed(() => {
    const required: Set<string> = this.requiredFileKeys();
    const items: { key: string; fileName: string; message: string; canRetry: boolean }[] = [];
    for (const [key, attempt] of this.attempts()) {
      if ((attempt.state === 'rejected' || attempt.state === 'failed') && required.has(key)) {
        items.push({ key, fileName: attempt.fileName, message: attempt.message ?? '', canRetry: attempt.state === 'failed' && this.retainedFiles.has(key) });
      }
    }
    return items;
  });

  // -- Collapsible sections ---------------------------------------------------
  /** What each section does until the user opens or closes it by hand. */
  private readonly sectionDefaults: Signal<Record<SectionId, boolean>> = computed(() => {
    const progress: ProcessingProgress = this.processing();
    const required: RequiredProgress = this.required();
    return {
      review: this.reviewSummary().issueCount > 0,
      activity: !progress.isFinished && progress.attemptedCount > 0
        || progress.needsAttentionCount > 0
        || (progress.attemptedCount === 0 && required.requiredFileCount > 0 && !required.areAllUploaded),
      unreferenced: this.unreferencedFiles().length > 0,
      storedUnreferenced: this.storedUnreferencedFiles().length > 0,
    };
  });
  readonly reviewHeading: Signal<string> = computed(() => {
    const summary: ReviewSummary = this.reviewSummary();
    if (summary.issueCount > 0) {
      return `Review status · ${summary.issueCount} ${summary.issueCount === 1 ? 'issue' : 'issues'}`;
    }
    return summary.isHealthy ? 'Review status · No issues found' : 'Review status · Upload in progress';
  });
  readonly activityHeading: Signal<string> = computed(() => `Upload activity · ${this.activityState()}`);
  readonly unreferencedHeading: Signal<string> = computed(() => `Files not referenced by the index · ${this.unreferencedFiles().length}`);
  readonly storedUnreferencedHeading: Signal<string> = computed(() => `Uploaded files no longer referenced by the index · ${this.storedUnreferencedFiles().length}`);
  readonly indexHeading: Signal<string> = computed(() => `Index · ${this.rows().length} ${this.rows().length === 1 ? 'row' : 'rows'}`);

  get documentTypeLabel(): string {
    return this.type() === ShareLinkClass.PdfPipeClass ? 'Pipe Class' : 'Valve';
  }

  /** The number of records of the selected type in words, shared by the confirmation, the completed banner and the notification. */
  describeRecords(count: number): string {
    return describeRecordCount(this.documentTypeLabel, count);
  }

  ngOnInit(): void {
    const initialPreview: DocumentUploadPreviewViewModel = this.preview();
    this.jobId.set(initialPreview.jobId ?? '');
    this.type.set(initialPreview.type ?? ShareLinkClass.PdfPipeClass);
    this.applyPreview(initialPreview);
    this.activeChange.emit(this.isEditable());

    this.watchRowChanges();
    this.sendInitialDocuments();
  }

  // -- Template helpers ------------------------------------------------------

  /** The value of one cell, tolerating rows shorter than the column list. */
  getCellValue(row: PreviewRow, columnIndex: number): string {
    return row.values[columnIndex] ?? '';
  }

  getCellIssues(rowId: number, columnKey: string): DocumentUploadIssueViewModel[] {
    return this.issueIndex().byCell.get(cellKey(rowId, columnKey)) ?? [];
  }

  getRowIssues(rowId: number): DocumentUploadIssueViewModel[] {
    return this.issueIndex().byRow.get(rowId) ?? [];
  }

  /** Issues about the upload as a whole, not tied to a row or a file. */
  getGeneralIssues(): DocumentUploadIssueViewModel[] {
    return this.issueIndex().general;
  }

  hasError(issues: ReadonlyArray<DocumentUploadIssueViewModel>): boolean {
    return issues.some((issue: DocumentUploadIssueViewModel) => issue.severity === DocumentUploadIssueSeverity.Error);
  }

  joinMessages(issues: ReadonlyArray<DocumentUploadIssueViewModel>): string {
    return issues.map((issue: DocumentUploadIssueViewModel) => issue.message ?? '').join('\n');
  }

  getColumnHeader(columnKey: string): string {
    return this.columns().find((column: DocumentIndexColumnViewModel) => column.key === columnKey)?.header ?? columnKey;
  }

  /** Everything a row's status icon explains: the problems with its values, then the state of its file. */
  describeRow(view: RowView): string {
    const lines: string[] = this.getRowIssues(view.row.rowId)
      .filter((issue: DocumentUploadIssueViewModel) => issue.columnKey !== FILE_NAME_COLUMN_KEY)
      .map((issue: DocumentUploadIssueViewModel) => (issue.columnKey ? `${this.getColumnHeader(issue.columnKey)}: ${issue.message}` : (issue.message ?? '')));
    if (view.fileState !== 'uploaded') {
      lines.push(view.fileMessage);
    }
    return lines.join('\n');
  }

  /** The label of a row's file state; every state has a word, so color is never the only signal. */
  getRowFileStateLabel(state: RowFileState): string {
    return ROW_FILE_STATE_LABELS[state];
  }

  getRowFileStateIcon(state: RowFileState): string {
    return ROW_FILE_STATE_ICONS[state];
  }

  /** The text of the button that selects a file for a row. */
  getSelectFileLabel(view: RowView): string {
    switch (view.fileState) {
      case 'none':
      case 'missing':
        return 'Select file';
      case 'uploaded':
        return 'Replace file';
      default:
        return 'Select again';
    }
  }

  /** True when the page still holds the file of this row, so it can be sent again without selecting it. */
  canRetryRowFile(view: RowView): boolean {
    return view.fileState === 'failed' && this.retainedFiles.has(view.fileKey);
  }

  isSectionExpanded(section: SectionId): boolean {
    return this.sectionChoices()[section] ?? this.sectionDefaults()[section];
  }

  /** Opens or closes a section by hand. From then on the section keeps that choice whatever the validation or the upload does. */
  toggleSection(section: SectionId): void {
    const expanded: boolean = !this.isSectionExpanded(section);
    this.sectionChoices.update((choices: Partial<Record<SectionId, boolean>>) => ({ ...choices, [section]: expanded }));
  }

  /** The sort state of one column header: only the column the table is sorted by is ascending or descending. */
  getSortState(columnKey: string): 'ascending' | 'descending' | 'none' {
    const sort: RowSort = this.sortState();
    if (sort.active !== columnKey || !sort.direction) {
      return 'none';
    }
    return sort.direction === 'desc' ? 'descending' : 'ascending';
  }

  /** The icon drawn in a sortable header: one arrow for the active column, a neutral two-way mark for the others. */
  getSortIcon(columnKey: string): string {
    const state: 'ascending' | 'descending' | 'none' = this.getSortState(columnKey);
    return state === 'ascending' ? 'arrow_upward' : state === 'descending' ? 'arrow_downward' : 'unfold_more';
  }

  /** Applies the sort the user chose in a column header. Only the order of display changes. */
  onSortChange(sort: Sort): void {
    this.sortState.set({ active: sort.active, direction: sort.direction });
    this.pageIndex.set(0);
  }

  trackByRowView(_index: number, view: RowView): number {
    return view.row.rowId;
  }

  onPage(event: PageEvent): void {
    this.pageIndex.set(event.pageIndex);
    this.pageSize.set(event.pageSize);
  }

  /** Shows the page of the table that holds the row and focuses it, so the user can fix it right away. */
  goToRow(rowId: number): void {
    const place: number = this.sortedRowViews().findIndex((view: RowView) => view.row.rowId === rowId);
    if (place < 0) {
      return;
    }

    this.pageIndex.set(Math.floor(place / this.pageSize()));
    // The page is re-rendered first; focus the row on the next macrotask.
    setTimeout(() => {
      const target: HTMLElement | null = document.getElementById(`row-${rowId}`);
      target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target?.focus();
    });
  }

  /** Opens a section (the user's choice from then on) and brings it into view. */
  showSection(section: SectionId): void {
    this.sectionChoices.update((choices: Partial<Record<SectionId, boolean>>) => ({ ...choices, [section]: true }));
    setTimeout(() => document.getElementById(`section-${section}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }

  /** Takes the user to what one line of the review status is about: the first affected row, or the section that lists the files. */
  goToReviewCategory(categoryId: string): void {
    const firstRowWhere = (predicate: (view: RowView) => boolean): void => {
      const view: RowView | undefined = this.sortedRowViews().find(predicate);
      if (view) {
        this.goToRow(view.row.rowId);
      }
    };

    switch (categoryId) {
      case 'rowsNeedingFile':
        firstRowWhere((view: RowView) => view.fileState === 'none' || view.fileState === 'missing');
        break;
      case 'rowsWithErrors':
        firstRowWhere((view: RowView) => view.dataErrorCount > 0);
        break;
      case 'warnings':
        firstRowWhere((view: RowView) => view.dataWarningCount > 0);
        break;
      case 'filesNotReferenced':
      case 'filesRepeated':
        this.showSection('unreferenced');
        break;
      case 'filesNoLongerUsed':
        this.showSection('storedUnreferenced');
        break;
      case 'filesFailed':
      case 'filesNotValid':
      case 'filesUploading':
        this.showSection('activity');
        break;
      default:
        break;
    }
  }

  // ── Row editing ────────────────────────────────────────────────────────────

  onCellInput(rowId: number, columnIndex: number, event: Event): void {
    // A file name is chosen with a file, never typed: a name without the bytes of a file is not a valid final state.
    if (columnIndex === this.fileNameColumnIndex()) {
      return;
    }

    const value: string = (event.target as HTMLInputElement).value;
    this.rows.update((rows: PreviewRow[]) => rows.map((row: PreviewRow) => {
      if (row.rowId !== rowId) {
        return row;
      }
      const values: string[] = [...row.values];
      values[columnIndex] = value;
      return { rowId: row.rowId, values };
    }));
    this.markRowsEdited();
  }

  addRow(): void {
    const nextRowId: number = this.nextRowId();
    const blankValues: string[] = this.columns().map(() => '');
    this.rows.update((rows: PreviewRow[]) => [...rows, { rowId: nextRowId, values: blankValues }]);
    this.showPageOfRow(nextRowId);
    this.markRowsEdited();
  }

  removeRow(rowId: number): void {
    this.rows.update((rows: PreviewRow[]) => rows.filter((row: PreviewRow) => row.rowId !== rowId));
    const lastPageIndex: number = Math.max(Math.ceil(this.rows().length / this.pageSize()) - 1, 0);
    this.pageIndex.set(Math.min(this.pageIndex(), lastPageIndex));
    this.markRowsEdited();
  }

  private nextRowId(): number {
    return this.rows().reduce((max: number, row: PreviewRow) => Math.max(max, row.rowId), 0) + 1;
  }

  /** Counts a change of the rows; returns the edit version that carries it. */
  private registerRowEdit(): number {
    this.editVersion++;
    this.requestEpoch++;
    this.hasRowEdits.set(true);
    this.hasPendingRowChanges.set(true);
    return this.editVersion;
  }

  private markRowsEdited(): void {
    this.registerRowEdit();
    this.rowChanges$.next();
  }

  /**
   * Sends the edited rows for validation after the user stops typing, or at once when an association asks for it.
   * `concatMap` runs the saves strictly one after another, so the server always ends up with the latest rows and a slow,
   * older response can never overwrite a newer one (`switchMap` would cancel the request client-side but not on the server).
   */
  private watchRowChanges(): void {
    merge(this.rowChanges$.pipe(debounceTime(ROW_SAVE_DEBOUNCE_MS)), this.flushRows$)
      .pipe(
        concatMap(() => this.saveRows()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private saveRows(): Observable<void> {
    const versionAtSend: number = this.editVersion;
    if (versionAtSend <= this.lastSavedVersion) {
      // An earlier save already carried every change.
      return of(undefined);
    }

    const request: UpdateDocumentIndexRowsRequest = new UpdateDocumentIndexRowsRequest({
      rows: this.rows().map((row: PreviewRow) => new DocumentIndexRowViewModel({ rowId: row.rowId, values: row.values })),
    });

    this.isSavingRows.set(true);
    // The result is announced after the save ended and its flags settled, so whatever waits for it (an association sending its file)
    // starts from a state in which the rows are no longer busy.
    let outcome: boolean | null = null;
    return this.esraPipingApiClient.updateDocumentUploadRows(this.jobId(), request).pipe(
      tap((update: DocumentUploadUpdateViewModel) => {
        this.applyUpdate(update);
        this.lastSavedVersion = Math.max(this.lastSavedVersion, versionAtSend);
        if (versionAtSend === this.editVersion) {
          this.hasPendingRowChanges.set(false);
        }
        outcome = true;
      }),
      map(() => undefined),
      // Keep the pending flag so the confirm button stays disabled; the next edit retries the save.
      catchError((err: unknown) => {
        this.handleError(err, 'Your changes could not be validated.');
        outcome = false;
        return of(undefined);
      }),
      finalize(() => {
        this.isSavingRows.set(false);
        if (outcome !== null) {
          this.rowSaved$.next({ version: versionAtSend, ok: outcome });
        }
      }),
    );
  }

  // ── Associating files with rows ────────────────────────────────────────────

  /** Opens the file dialog of the browser for one row. A single shared input serves every row, so hundreds of rows cost nothing. */
  selectFileForRow(rowId: number, fileInput: HTMLInputElement): void {
    if (!this.isEditable() || this.isBusy()) {
      return;
    }
    this.rowFileTarget = rowId;
    fileInput.click();
  }

  onRowFileSelected(event: Event): void {
    const input: HTMLInputElement = event.target as HTMLInputElement;
    const file: File | null = input.files?.[0] ?? null;
    input.value = '';
    const rowId: number | null = this.rowFileTarget;
    this.rowFileTarget = null;
    if (file && rowId !== null) {
      this.associateFile([rowId], file, false);
    }
  }

  /** Sends the file of a row again after its upload failed, using the file the page still holds. */
  retryRowFile(view: RowView): void {
    const file: File | undefined = this.retainedFiles.get(view.fileKey);
    if (!file) {
      this.snackBarService.showError('The file is no longer in this browser tab. Select the file again.');
      return;
    }
    this.uploadDocuments([file]);
  }

  /** True for a row that has no successfully uploaded file, which is the only kind of row that can take an existing file. */
  canAssociateExistingFile(view: RowView): boolean {
    return view.fileState === 'none' || view.fileState === 'missing' || view.fileState === 'failed' || view.fileState === 'rejected';
  }

  /**
   * Lets the user pick a file the page already knows for one row, then associates it with `associateFile`, the same workflow as
   * Associate to row: the row is saved first, a file that is not uploaded yet is sent once, and a file that is uploaded is only reused.
   */
  associateExistingFile(view: RowView): void {
    if (!this.isEditable() || this.isBusy()) {
      return;
    }

    const candidates: FileCandidate[] = this.buildFileCandidates(view);
    if (candidates.length === 0) {
      this.snackBarService.showInfo('No existing files are available to associate. Select a file from your computer instead.');
      return;
    }

    this.dialog.open(BuPdfFilePickerDialogComponent, {
      width: '560px',
      maxWidth: '95vw',
      data: { candidates },
    }).afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((chosen: FileCandidate | undefined) => {
      if (chosen) {
        this.associateFile([view.row.rowId], chosen.isUploaded ? chosen.fileName : this.retainedFiles.get(chosen.key) ?? chosen.fileName, false);
      }
    });
  }

  /** The files a row can take: files the page holds but never uploaded, uploaded files no row names, and uploaded files other rows share. */
  private buildFileCandidates(target: RowView): FileCandidate[] {
    const candidates: FileCandidate[] = this.unreferencedFiles()
      .filter((item: UnreferencedFile) => item.canAssociate)
      .map((item: UnreferencedFile): FileCandidate => ({ key: item.key, fileName: item.fileName, isUploaded: false, usedByRows: 0 }));
    candidates.push(...this.storedUnreferencedFiles().map((item: StoredUnreferencedFile): FileCandidate => ({ key: item.key, fileName: item.fileName, isUploaded: true, usedByRows: 0 })));
    const listed: Set<string> = new Set<string>(candidates.map((candidate: FileCandidate) => candidate.key));
    for (const view of this.rowViews()) {
      if (view.fileState === 'uploaded' && view.fileKey !== target.fileKey && !listed.has(view.fileKey)) {
        listed.add(view.fileKey);
        candidates.push({ key: view.fileKey, fileName: view.fileName, isUploaded: true, usedByRows: view.sharedCount });
      }
    }
    return candidates;
  }

  /** Lets the user choose the rows of the index that refer to a file the index did not reference, then associates it with them. */
  associateUnreferencedFile(item: UnreferencedFile): void {
    const file: File | undefined = this.retainedFiles.get(item.key);
    if (!file || !this.isEditable()) {
      this.snackBarService.showError('The file is no longer in this browser tab. Select the file again.');
      return;
    }

    this.dialog.open(BuPdfRowPickerDialogComponent, {
      width: '760px',
      maxWidth: '95vw',
      data: { fileName: file.name, candidates: this.buildCandidates() },
    }).afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result: RowPickerResult | undefined) => {
      if (result && (result.rowIds.length > 0 || result.addNewRow)) {
        this.associateFile(result.rowIds, file, result.addNewRow);
      }
    });
  }

  /** Forgets a file the page holds but the server never stored. Nothing on the server is touched. */
  removeUnreferencedFile(item: UnreferencedFile): void {
    this.forgetTransfer(item.key);
  }

  /** Lets the user choose the rows of the index that should refer to a file that is already uploaded, then associates it with them. Nothing is sent. */
  associateStoredFile(item: StoredUnreferencedFile): void {
    if (!this.isEditable()) {
      return;
    }

    this.dialog.open(BuPdfRowPickerDialogComponent, {
      width: '760px',
      maxWidth: '95vw',
      data: { fileName: item.fileName, candidates: this.buildCandidates() },
    }).afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result: RowPickerResult | undefined) => {
      if (result && (result.rowIds.length > 0 || result.addNewRow)) {
        this.associateFile(result.rowIds, item.fileName, result.addNewRow);
      }
    });
  }

  /**
   * Removes an uploaded file that no row refers to from this upload, but only after the user agreed. The server keeps it temporarily before
   * it is deleted, which the dialog says; nothing else ever removes an uploaded file without a decision.
   */
  removeStoredFile(item: StoredUnreferencedFile): void {
    if (!this.isEditable() || this.isBusy()) {
      return;
    }

    this.dialog.open(ConfirmDialogComponent, {
      width: '520px',
      data: {
        title: 'Remove file from this upload?',
        dialogMessage: `'${item.fileName}' is no longer referenced by the index. Removing it will exclude it from this upload. The file will be retained temporarily before it is deleted.`,
        confirmText: 'Remove from this upload',
        cancelText: 'Keep file',
      },
    }).afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.removeStoredFileOnServer(item);
      }
    });
  }

  private removeStoredFileOnServer(item: StoredUnreferencedFile): void {
    this.requestEpoch++;
    this.isFileOperationRunning.set(true);
    this.uploadProgressService.reset();
    this.esraPipingApiClient.removeDocumentUploadDocument(this.jobId(), item.fileName)
      .pipe(
        finalize(() => this.isFileOperationRunning.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (update: DocumentUploadUpdateViewModel) => {
          this.forgetTransfer(item.key);
          this.applyUpdate(update);
        },
        error: (err: unknown) => this.handleError(err, 'The file could not be removed from this upload.'),
      });
  }

  /** The rows a file can be associated with: the ones that have no file, or whose file is not uploaded. */
  private buildCandidates(): RowCandidate[] {
    const identifierIndex: number = this.columns().length > 0 ? 0 : -1;
    const businessUnitIndex: number = this.columnKeys().indexOf('BusinessUnit');
    return this.rowViews()
      .filter((view: RowView) => view.fileState !== 'uploaded' && view.fileState !== 'uploading')
      .map((view: RowView): RowCandidate => ({
        rowId: view.row.rowId,
        excelRow: view.excelRow,
        identifier: identifierIndex >= 0 ? view.row.values[identifierIndex] ?? '' : '',
        businessUnit: businessUnitIndex >= 0 ? view.row.values[businessUnitIndex] ?? '' : '',
        fileName: view.fileName,
        issue: view.fileState === 'none' ? 'No file selected' : ROW_FILE_STATE_LABELS[view.fileState],
      }));
  }

  /**
   * Associates one file with rows of the index and, when the file is still in the browser, uploads it, in this order and only in this order:
   * 1. the rows get the file's real name, and the whole row set is saved and validated in one request;
   * 2. only when that succeeded and the server lists the name as expected, the file is sent, once, whatever the number of rows.
   * The upload endpoint refuses names the index does not reference, so sending the file before step 1 ended could only fail.
   * A failed save sends nothing, restores the rows and leaves the file where it was, so the user can correct the rows or give up.
   * A file that is already uploaded (only its name is known) is associated without sending anything.
   * @param rowIds The rows that refer to the file now.
   * @param target The file, whose name the rows will carry; or the name of a file that is already uploaded.
   * @param addNewRow True to add a new row for the file as well; the user fills in its other values.
   */
  private associateFile(rowIds: ReadonlyArray<number>, target: File | string, addNewRow: boolean): void {
    const fileNameIndex: number = this.fileNameColumnIndex();
    if (!this.isEditable() || this.isJobLost() || fileNameIndex < 0) {
      return;
    }

    const file: File | null = typeof target === 'string' ? null : target;
    const fileName: string = typeof target === 'string' ? target : target.name;
    if (file) {
      const { accepted, unsupportedNames, oversizedNames } = filterAcceptedDocuments([file]);
      if (accepted.length === 0) {
        this.snackBarService.showError(unsupportedNames.length > 0
          ? `Unsupported file type. Accepted: ${this.allowedDocumentExtensions.join(', ')}.`
          : `Files must be ${MAX_FILE_SIZE_MB} MB or smaller (${oversizedNames.join(', ')}).`);
        return;
      }
      this.retainedFiles.set(fileKey(fileName), file);
    }

    const key: string = fileKey(fileName);
    const previousNames: Map<number, string> = new Map<number, string>();
    const newRowId: number | null = addNewRow ? this.nextRowId() : null;
    this.rows.update((rows: PreviewRow[]) => {
      const updated: PreviewRow[] = rows.map((row: PreviewRow) => {
        if (!rowIds.includes(row.rowId)) {
          return row;
        }
        previousNames.set(row.rowId, row.values[fileNameIndex] ?? '');
        const values: string[] = [...row.values];
        values[fileNameIndex] = fileName;
        return { rowId: row.rowId, values };
      });
      if (newRowId !== null) {
        const values: string[] = this.columns().map(() => '');
        values[fileNameIndex] = fileName;
        updated.push({ rowId: newRowId, values });
      }
      return updated;
    });
    if (newRowId !== null) {
      this.showPageOfRow(newRowId);
    }

    // Subscribe before asking for the save: a fast answer must not be missed.
    const version: number = this.registerRowEdit();
    this.rowSaved$.pipe(
      filter((saved: RowSaveResult) => saved.version >= version),
      take(1),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((saved: RowSaveResult) => this.continueAssociation(saved, key, file, previousNames, newRowId));
    this.flushRows$.next();
  }

  /** The second half of an association: sends the file once the server has accepted the rows, or undoes the rows when it did not. */
  private continueAssociation(saved: RowSaveResult, key: string, file: File | null, previousNames: ReadonlyMap<number, string>, newRowId: number | null): void {
    if (!saved.ok) {
      this.restoreRows(previousNames, newRowId);
      this.snackBarService.showError(file
        ? 'The rows could not be saved, so the file was not uploaded. The file is still listed; try again.'
        : 'The rows could not be saved, so the file was not associated. Try again.');
      return;
    }

    const isExpectedByServer: boolean = this.files().some((serverFile: DocumentUploadFileViewModel) => fileKey(serverFile.fileName ?? '') === key);
    if (!isExpectedByServer) {
      this.snackBarService.showError('The index does not reference this file yet. Check the rows and try again.');
      return;
    }

    if (file) {
      this.uploadDocuments([file]);
    }
  }

  private restoreRows(previousNames: ReadonlyMap<number, string>, newRowId: number | null): void {
    const fileNameIndex: number = this.fileNameColumnIndex();
    this.rows.update((rows: PreviewRow[]) => rows
      .filter((row: PreviewRow) => row.rowId !== newRowId)
      .map((row: PreviewRow) => {
        const previous: string | undefined = previousNames.get(row.rowId);
        if (previous === undefined) {
          return row;
        }
        const values: string[] = [...row.values];
        values[fileNameIndex] = previous;
        return { rowId: row.rowId, values };
      }));
    this.markRowsEdited();
  }

  /** Brings the page of the table that shows a row into view, without moving the keyboard focus. */
  private showPageOfRow(rowId: number): void {
    const place: number = this.sortedRowViews().findIndex((view: RowView) => view.row.rowId === rowId);
    if (place >= 0) {
      this.pageIndex.set(Math.floor(place / this.pageSize()));
    }
  }

  // ── Documents ──────────────────────────────────────────────────────────────

  onDocumentsSelected(event: Event): void {
    const input: HTMLInputElement = event.target as HTMLInputElement;
    this.addDocuments(input.files);
    input.value = '';
  }

  onDocumentsDropped(event: DragEvent): void {
    event.preventDefault();
    this.isDraggingDocuments.set(false);
    this.addDocuments(event.dataTransfer?.files ?? null);
  }

  onDocumentsDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDraggingDocuments.set(true);
  }

  onDocumentsDragLeave(): void {
    this.isDraggingDocuments.set(false);
  }

  /**
   * Sends the files whose upload failed again, one batch at a time like the first attempt. A file the browser no longer has (the page
   * was refreshed) cannot be retried: the user selects it again.
   */
  retryFailedUploads(): void {
    const failedKeys: string[] = [...this.attempts()].filter(([, attempt]: [string, UploadAttempt]) => attempt.state === 'failed').map(([key]: [string, UploadAttempt]) => key);
    const files: File[] = failedKeys.map((key: string) => this.retainedFiles.get(key)).filter((file: File | undefined): file is File => file !== undefined);

    if (files.length < failedKeys.length) {
      this.snackBarService.showError('Some files are no longer in this browser tab. Select those files again.');
    }
    if (files.length > 0) {
      this.uploadDocuments(files);
    }
  }

  private addDocuments(fileList: FileList | null): void {
    if (!fileList || fileList.length === 0 || !this.isEditable() || this.isBusy()) {
      return;
    }

    const { accepted, unsupportedNames, oversizedNames } = filterAcceptedDocuments(Array.from(fileList));
    if (unsupportedNames.length > 0) {
      this.snackBarService.showError(`Unsupported file type. Accepted: ${this.allowedDocumentExtensions.join(', ')}. Skipped: ${unsupportedNames.join(', ')}`);
    }
    if (oversizedNames.length > 0) {
      this.snackBarService.showError(`Files must be ${MAX_FILE_SIZE_MB} MB or smaller. Skipped: ${oversizedNames.join(', ')}`);
    }
    this.uploadSelection(accepted);
  }

  /**
   * Sends one selection of files. A name that more than one selected file carries is ambiguous: none of those files is sent, each name is
   * reported, and the user keeps one file and selects again. A name that is already uploaded is an intentional replacement.
   */
  private uploadSelection(files: ReadonlyArray<File>): void {
    const { unique, ambiguousNames } = splitAmbiguousFileNames(files);
    for (const name of ambiguousNames) {
      this.recordRepeatedName(name);
    }
    if (ambiguousNames.length > 0) {
      this.snackBarService.showError(ambiguousNames.map((name: string) => describeAmbiguousFileName(name)).join(' '));
    }
    this.uploadDocuments(unique);
  }

  /** Records that a name was refused before sending, because more than one selected file carries it. Nothing reaches the server. */
  private recordRepeatedName(fileName: string): void {
    const key: string = fileKey(fileName);
    this.retainedFiles.delete(key);
    this.setAttempt(key, { fileName, sizeInBytes: 0, state: 'rejected', message: describeAmbiguousFileName(fileName), isRepeatedName: true });
    this.setTransfer(key, { fileName, sizeInBytes: 0, state: 'rejected', message: describeAmbiguousFileName(fileName) });
  }

  // -- Document transfer -------------------------------------------------------

  /** Sends the documents selected next to the index file, once, right after the preview opened. */
  private sendInitialDocuments(): void {
    const initial: ReadonlyArray<File> = this.initialDocuments();
    if (initial.length > 0 && this.isEditable()) {
      this.uploadSelection(initial);
    }
  }

  /**
   * Sends documents in bounded batches (a few files each, a few requests at a time) while the user keeps reviewing the rows.
   * `mergeMap` with a concurrency limit keeps the number of requests in flight bounded; each response says per file what happened.
   * A file that is already being sent is not sent twice, and the transfer ends with one authoritative read of the job.
   * @param files The files to send. A file whose name is already uploaded replaces it, so callers only pass files the user chose.
   */
  private uploadDocuments(files: ReadonlyArray<File>): void {
    const toSend: File[] = files.filter((file: File) => this.transfers().get(fileKey(file.name))?.state !== 'uploading');
    if (toSend.length === 0) {
      return;
    }

    // Rows keep their order while the files arrive; they are sorted again once the transfer ended.
    this.frozenOrder ??= this.sortedRowViews().map((view: RowView) => view.row.rowId);
    for (const file of toSend) {
      const key: string = fileKey(file.name);
      this.retainedFiles.set(key, file);
      this.setAttempt(key, { fileName: file.name, sizeInBytes: file.size, state: 'uploading' });
      this.setTransfer(key, { fileName: file.name, sizeInBytes: file.size, state: 'uploading' });
    }

    const hadFailure: { value: boolean } = { value: false };
    from(createUploadBatches(toSend))
      .pipe(
        mergeMap((batch: File[]) => this.sendBatch(batch, hadFailure), DOCUMENT_UPLOAD_MAX_CONCURRENT_REQUESTS),
        finalize(() => {
          if (hadFailure.value) {
            this.snackBarService.showError('Some files could not be uploaded. Try again, or select the files again.');
          }
          this.refreshAfterTransfer();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  /** One document request. Its answer lists what happened to each of its files; a failed request fails each of them, never more. */
  private sendBatch(batch: File[], hadFailure: { value: boolean }): Observable<void> {
    const parameters: FileParameter[] = batch.map((file: File) => ({ data: file, fileName: file.name }));
    this.runningTransferRequests.update((running: number) => running + 1);
    return this.esraPipingApiClient.addDocumentUploadDocuments(this.jobId(), parameters).pipe(
      tap((update: DocumentUploadUpdateViewModel) => this.applyBatchResults(batch, update)),
      map(() => undefined),
      catchError((err: unknown) => {
        hadFailure.value = true;
        this.failBatch(batch, err);
        return EMPTY;
      }),
      finalize(() => this.runningTransferRequests.update((running: number) => running - 1)),
    );
  }

  /**
   * Records the per-file results of one batch. Only these files change here, so a response that arrives late cannot undo what another
   * batch just uploaded. The validation is applied too, unless a row save is about to return a newer one.
   */
  private applyBatchResults(batch: File[], update: DocumentUploadUpdateViewModel): void {
    const results: Map<string, DocumentUploadFileViewModel> = new Map<string, DocumentUploadFileViewModel>(
      (update.results ?? []).map((result: DocumentUploadFileViewModel): [string, DocumentUploadFileViewModel] => [fileKey(result.fileName ?? ''), result]));
    const required: Set<string> = this.requiredFileKeys();

    this.transfers.update((transfers: Map<string, TransferEntry>) => {
      const next: Map<string, TransferEntry> = new Map<string, TransferEntry>(transfers);
      for (const file of batch) {
        const key: string = fileKey(file.name);
        next.set(key, this.toTransferEntry(file, results.get(key), required.has(key)));
      }
      return next;
    });
    this.attempts.update((attempts: Map<string, UploadAttempt>) => {
      const next: Map<string, UploadAttempt> = new Map<string, UploadAttempt>(attempts);
      for (const file of batch) {
        const key: string = fileKey(file.name);
        const entry: TransferEntry | undefined = this.transfers().get(key);
        if (entry) {
          next.set(key, { fileName: entry.fileName, sizeInBytes: entry.sizeInBytes, state: entry.state, message: entry.message });
        }
        if (entry?.state === 'uploaded') {
          this.retainedFiles.delete(key);
        }
      }
      return next;
    });

    if (!this.hasPendingRowChanges() && !this.isSavingRows() && update.validation) {
      this.validation.set(update.validation);
    }
  }

  private toTransferEntry(file: File, result: DocumentUploadFileViewModel | undefined, isReferenced: boolean): TransferEntry {
    switch (result?.state) {
      case DocumentUploadFileState.Uploaded:
        return { fileName: file.name, sizeInBytes: file.size, state: 'uploaded' };
      case DocumentUploadFileState.Rejected:
        return { fileName: file.name, sizeInBytes: file.size, state: 'rejected', message: describeRejection(result.message, isReferenced) };
      default:
        // Failed, or not answered at all: nothing was recorded for the file, so it is safe to send again.
        return { fileName: file.name, sizeInBytes: file.size, state: 'failed', message: FILE_MESSAGES.failed };
    }
  }

  private failBatch(batch: File[], err: unknown): void {
    if (err instanceof ApiException && err.status === 404) {
      this.isJobLost.set(true);
      this.activeChange.emit(false);
    }
    for (const file of batch) {
      const entry: TransferEntry = { fileName: file.name, sizeInBytes: file.size, state: 'failed', message: FILE_MESSAGES.failed };
      this.setTransfer(fileKey(file.name), entry);
      this.setAttempt(fileKey(file.name), { fileName: file.name, sizeInBytes: file.size, state: 'failed', message: FILE_MESSAGES.failed });
    }
  }

  private setTransfer(key: string, entry: TransferEntry): void {
    this.transfers.update((transfers: Map<string, TransferEntry>) => new Map<string, TransferEntry>(transfers).set(key, entry));
  }

  private setAttempt(key: string, attempt: UploadAttempt): void {
    this.attempts.update((attempts: Map<string, UploadAttempt>) => new Map<string, UploadAttempt>(attempts).set(key, attempt));
  }

  private forgetTransfer(key: string): void {
    this.retainedFiles.delete(key);
    this.transfers.update((transfers: Map<string, TransferEntry>) => {
      const next: Map<string, TransferEntry> = new Map<string, TransferEntry>(transfers);
      next.delete(key);
      return next;
    });
    this.attempts.update((attempts: Map<string, UploadAttempt>) => {
      const next: Map<string, UploadAttempt> = new Map<string, UploadAttempt>(attempts);
      next.delete(key);
      return next;
    });
  }

  /**
   * After the last request of a transfer, reads the job once: the server's list is authoritative, so the page keeps only what the server
   * cannot show (a failed or rejected file that is not uploaded there). The rows the user may have edited meanwhile are never replaced.
   */
  private refreshAfterTransfer(): void {
    if (this.isTransferring() || this.isJobLost() || this.isClosed) {
      return;
    }

    this.esraPipingApiClient.getDocumentUpload(this.jobId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (preview: DocumentUploadPreviewViewModel) => {
          if (this.isTransferring()) {
            return;
          }
          this.applyStatus(preview.status ?? this.status());
          if (!this.hasPendingRowChanges() && !this.isSavingRows()) {
            this.validation.set(preview.validation ?? EMPTY_VALIDATION);
            this.serverCanCommit.set(preview.canCommit ?? false);
          }
          this.dropConfirmedTransfers();
        },
        error: (err: unknown) => this.handleError(err, 'The upload could not be checked.'),
      });
  }

  /** Forgets the transfers the server now confirms as uploaded; failed and rejected ones stay until the user acts on them. */
  private dropConfirmedTransfers(): void {
    this.transfers.update((transfers: Map<string, TransferEntry>) => {
      const next: Map<string, TransferEntry> = new Map<string, TransferEntry>(transfers);
      for (const [key, entry] of transfers) {
        if (entry.state === 'uploaded') {
          next.delete(key);
        }
      }
      return next;
    });
  }

  /** Merges the server's file list with what this page knows about the files it sent. */
  private mergeTransfers(serverFiles: ReadonlyArray<DocumentUploadFileViewModel>, transfers: ReadonlyMap<string, TransferEntry>): DisplayFile[] {
    return serverFiles.map((file: DocumentUploadFileViewModel): DisplayFile => this.overlayTransfer(file, transfers.get(fileKey(file.fileName ?? ''))));
  }

  private overlayTransfer(file: DocumentUploadFileViewModel, entry: TransferEntry | undefined): DisplayFile {
    const state: DocumentUploadFileState = file.state ?? DocumentUploadFileState.Missing;
    const display: DisplayFile = {
      fileName: file.fileName ?? '',
      sizeInBytes: file.sizeInBytes ?? 0,
      state,
      isUploading: false,
      message: state === DocumentUploadFileState.Rejected ? describeRejection(file.message, true) : file.message,
    };
    if (!entry) {
      return display;
    }

    switch (entry.state) {
      case 'uploading':
        return { ...display, isUploading: true, sizeInBytes: entry.sizeInBytes };
      case 'uploaded':
        return { ...display, state: DocumentUploadFileState.Uploaded, sizeInBytes: entry.sizeInBytes, message: undefined };
      case 'failed':
        // A failed replacement leaves the file uploaded before in place; only a file that is not uploaded shows as failed.
        return display.state === DocumentUploadFileState.Uploaded
          ? { ...display, message: `The new file could not be uploaded. The file uploaded before is kept. ${entry.message ?? ''}`.trim() }
          : { ...display, state: DocumentUploadFileState.Failed, sizeInBytes: entry.sizeInBytes, message: entry.message };
      default:
        return display.state === DocumentUploadFileState.Uploaded
          ? { ...display, message: `The new file was not accepted. The file uploaded before is kept. ${entry.message ?? ''}`.trim() }
          : { ...display, state: DocumentUploadFileState.Rejected, sizeInBytes: entry.sizeInBytes, message: entry.message };
    }
  }

  // ── Index file ─────────────────────────────────────────────────────────────

  onIndexFileSelected(event: Event): void {
    const input: HTMLInputElement = event.target as HTMLInputElement;
    const file: File | null = input.files?.[0] ?? null;
    input.value = '';
    if (!file || !this.isEditable() || this.isBusy()) {
      return;
    }

    if (!file.name.toLowerCase().endsWith(INDEX_FILE_EXTENSION)) {
      this.snackBarService.showError('The index file must be an Excel workbook (.xlsx).');
      return;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      this.snackBarService.showError(`The index file exceeds the maximum size of ${MAX_FILE_SIZE_MB} MB.`);
      return;
    }

    if (!this.hasRowEdits()) {
      this.replaceIndexFile(file);
      return;
    }

    this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {
        dialogMessage: 'Replacing the index file discards the changes you made to the rows. Files that match the new index are kept. Uploaded files not referenced by the new index can be reviewed before confirmation. Continue?',
        confirmText: 'replace',
        cancelText: 'keep my changes',
      },
    }).afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.replaceIndexFile(file);
      }
    });
  }

  private replaceIndexFile(file: File): void {
    this.requestEpoch++;
    this.isFileOperationRunning.set(true);
    this.uploadProgressService.reset();
    this.esraPipingApiClient.replaceDocumentUploadIndexFile(this.jobId(), { data: file, fileName: file.name })
      .pipe(
        finalize(() => this.isFileOperationRunning.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (preview: DocumentUploadPreviewViewModel) => {
          this.applyPreview(preview);
          this.snackBarService.showInfo('The index file was replaced.');
        },
        error: (err: unknown) => this.handleError(err, 'The index file could not be replaced.'),
      });
  }

  // ── Confirm / discard ──────────────────────────────────────────────────────

  confirm(): void {
    if (!this.canConfirm()) {
      return;
    }

    const unreferencedCount: number = this.unreferencedFiles().length;
    if (unreferencedCount > 0) {
      this.confirmWithUnreferencedFiles(unreferencedCount);
      return;
    }

    if (this.storedUnreferencedFiles().length > 0) {
      this.confirmWithStoredUnreferencedFiles();
      return;
    }

    const warningNote: string = this.warningCount() > 0 ? ` There are ${this.warningCount()} warning(s) you may want to review first.` : '';
    this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {
        dialogMessage: `Are you sure you want to create ${this.describeRecords(this.rows().length)}?`
          + ` A document that replaces a current one archives it.${warningNote}`,
        confirmText: 'create records',
        cancelText: 'cancel',
      },
    }).afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.commit();
      }
    });
  }

  /**
   * Tells the user that the files no row refers to are not part of the upload, before the records are created. They were refused before
   * anything was stored, so the message says only that: nothing was uploaded for them and nothing is created from them.
   * @param count How many selected files are not referenced by the index.
   */
  private confirmWithUnreferencedFiles(count: number): void {
    this.dialog.open(ConfirmDialogComponent, {
      width: '520px',
      data: {
        title: 'Some selected files will not be included',
        dialogMessage: `${count} selected ${count === 1 ? 'file is' : 'files are'} not referenced by the index and ${count === 1 ? 'was' : 'were'} not uploaded.`
          + ' Continuing will create records only from the validated index rows and their uploaded files.',
        confirmText: 'Continue',
        cancelText: 'Review files',
      },
    }).afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((confirmed: boolean) => {
      if (!confirmed) {
        this.showSection('unreferenced');
      } else if (this.storedUnreferencedFiles().length > 0) {
        this.confirmWithStoredUnreferencedFiles();
      } else {
        this.commit();
      }
    });
  }

  /**
   * Asks the user to decide about the uploaded files that no row refers to any more. Only an explicit Continue and remove sends the
   * consent, together with the names the user was shown; the server recomputes the files from the rows and refuses a decision that no longer matches.
   */
  private confirmWithStoredUnreferencedFiles(): void {
    const shown: string[] = this.storedUnreferencedFiles().map((file: StoredUnreferencedFile) => file.fileName);
    const count: number = shown.length;
    this.dialog.open(ConfirmDialogComponent, {
      width: '520px',
      data: {
        title: 'Some uploaded files will not be included',
        dialogMessage: `${count} uploaded ${count === 1 ? 'file is' : 'files are'} no longer referenced by the index. If you continue, ${count === 1 ? 'it' : 'they'} will be`
          + ' removed from this upload and retained temporarily before deletion.',
        confirmText: 'Continue and remove',
        cancelText: 'Review files',
      },
    }).afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.commit(shown);
      } else {
        this.showSection('storedUnreferenced');
      }
    });
  }

  /**
   * Confirms the upload with one request and waits for its real answer: the records exist when it succeeds, and nothing was created
   * when it fails. The page is blocked while it runs (`isCommitting`), and there is nothing to poll afterwards.
   * @param discardedFileNames The uploaded files the user agreed to remove from this upload; absent when there is nothing to decide.
   */
  private commit(discardedFileNames?: ReadonlyArray<string>): void {
    this.requestEpoch++;
    this.isCommitting.set(true);
    const request: DocumentUploadCommitRequest = new DocumentUploadCommitRequest(
      discardedFileNames
        ? { discardUnreferencedUploadedFiles: true, unreferencedFileNames: [...discardedFileNames] }
        : { discardUnreferencedUploadedFiles: false });
    this.esraPipingApiClient.commitDocumentUpload(this.jobId(), request)
      .pipe(
        indicate(this.confirming$),
        finalize(() => this.isCommitting.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (result: DocumentUploadCommitResultViewModel) => this.applyCommitResult(result),
        error: (err: unknown) => this.handleCommitError(err),
      });
  }

  private applyCommitResult(result: DocumentUploadCommitResultViewModel): void {
    this.serverCanCommit.set(false);
    this.applyStatus(result.status ?? new DocumentUploadStatusViewModel({ ...EMPTY_STATUS, state: DocumentUploadJobState.Completed, createdRecordIds: result.createdRecordIds }));
    this.snackBarService.showInfo(`${this.describeRecords(result.createdRecordIds?.length ?? 0)} created.`);
  }

  /**
   * Shows why the records were not created. The failure is structured when the server could explain it (validation errors, documents
   * not ready, upload changed, failed); anything else is a message or a lost job. Either way the preview is read again, because the
   * server may know more than the page does.
   */
  private handleCommitError(err: unknown): void {
    if (err instanceof DocumentUploadCommitFailureViewModel) {
      this.snackBarService.showError(err.message ?? 'The upload could not be confirmed.');
      if (err.validation) {
        this.validation.set(err.validation);
      }
      this.reloadPreview();
      return;
    }

    this.handleError(err, 'The upload could not be confirmed.');
    // `handleError` already reads the job again after a conflict.
    const isConflict: boolean = err instanceof ApiException && err.status === 409;
    if (!this.isJobLost() && !isConflict) {
      this.reloadPreview();
    }
  }

  /** Reads the job again, e.g. after a failed confirmation or when the user asks to check the upload. */
  refresh(): void {
    this.reloadPreview();
  }

  discard(): void {
    this.dialog.open(ConfirmDialogComponent, {
      width: '450px',
      data: {
        dialogMessage: 'Discard this upload? Nothing will be created and the uploaded files will not be used.',
        confirmText: 'discard',
        cancelText: 'keep reviewing',
      },
    }).afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.discardJob();
      }
    });
  }

  private discardJob(): void {
    this.requestEpoch++;
    this.esraPipingApiClient.discardDocumentUpload(this.jobId())
      .pipe(
        indicate(this.confirming$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => this.close(false),
        // A job that is already gone needs no discarding.
        error: (err: unknown) => (err instanceof ApiException && err.status === 404 ? this.close(false) : this.handleError(err, 'The upload could not be discarded.')),
      });
  }

  /** Leaves the preview. Called after the records were created, the job was discarded, or the job was lost. */
  close(wasCompleted: boolean): void {
    this.isClosed = true;
    this.activeChange.emit(false);
    this.closed.emit(wasCompleted);
  }

  // ── Server results ─────────────────────────────────────────────────────────

  private applyPreview(preview: DocumentUploadPreviewViewModel): void {
    this.indexFileName.set(preview.indexFileName ?? '');
    this.columns.set(preview.columns ?? []);
    this.rows.set(toPreviewRows(preview.rows));
    this.validation.set(preview.validation ?? EMPTY_VALIDATION);
    this.serverCanCommit.set(preview.canCommit ?? false);
    this.hasRowEdits.set(false);
    this.hasPendingRowChanges.set(false);
    this.pageIndex.set(0);
    this.applyStatus(preview.status ?? EMPTY_STATUS);
  }

  private applyUpdate(update: DocumentUploadUpdateViewModel): void {
    this.validation.set(update.validation ?? EMPTY_VALIDATION);
    this.serverCanCommit.set(update.canCommit ?? false);
    this.applyStatus(update.status ?? this.status());
  }

  /** Stores the new status and tells the host whether the job still holds unconfirmed work. */
  private applyStatus(status: DocumentUploadStatusViewModel): void {
    const wasActive: boolean = this.jobState() === DocumentUploadJobState.Preview;
    this.status.set(status);
    const isActive: boolean = this.jobState() === DocumentUploadJobState.Preview;
    if (wasActive !== isActive) {
      this.activeChange.emit(isActive);
    }
  }

  /** Reads the whole job again. A response that arrives after the user changed something meanwhile is dropped as stale. */
  private reloadPreview(): void {
    const epochAtSend: number = this.requestEpoch;
    this.esraPipingApiClient.getDocumentUpload(this.jobId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (preview: DocumentUploadPreviewViewModel) => {
          if (epochAtSend === this.requestEpoch) {
            this.applyPreview(preview);
          }
        },
        error: (err: unknown) => this.handleError(err, 'The preview could not be refreshed.'),
      });
  }

  /**
   * Shows the problem of a failed call. The generated client raises `ApiException` with the HTTP status and the server's text in
   * `response`: 404 means the job is gone (expired or discarded), 409 means it changed or can no longer be changed (so it is read
   * again), 403 and 400 carry a message for the user.
   */
  private handleError(err: unknown, fallbackMessage: string): void {
    if (err instanceof ApiException && err.status === 404) {
      this.isJobLost.set(true);
      this.activeChange.emit(false);
      return;
    }
    const rawError: unknown = err instanceof ApiException ? err.response : err;
    this.snackBarService.showError(getApiErrorMessage(rawError, fallbackMessage));
    if (err instanceof ApiException && err.status === 409) {
      this.reloadPreview();
    }
  }
}
